<template>
    <LoaderComponent v-if="loading" />

    <div class="space-y-6">
        <!-- <ShipmentHeader /> -->

        <template v-if="packages.data && packages.data.length > 0">
            <div v-for="pkg in packages.data" :key="pkg.id"
                class="bg-white rounded-2xl shadow-sm border border-gray-500 hover:shadow-md transition-all duration-200">
                <!-- Card Header -->
                <div class="flex justify-between items-center bg-[#f6e2b5] px-3 sm:px-6 py-2 rounded-t-2xl">
                    <!-- Left Section: Package ID + Tracking -->
                    <div>
                        <h2 class="text-md sm:text-lg font-semibold text-gray-800"> Order #{{ pkg.id }}</h2>
                        <h2 class="text-sm sm:text-md font-semibold text-gray-800">{{ pkg.service_label }}</h2>
                        <div class="flex items-center gap-2 whitespace-nowrap">
                            <span class="text-sm sm:text-md font-semibold text-gray-800">Tracking No:</span>
                            <p class=" text-xs sm:text-md " v-if="pkg.tracking_number_out">
                                <a :href="'https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=' + pkg.tracking_number_out"
                                    target="_blank" v-if="pkg.carrier_code === 'fedex'">{{ pkg.tracking_number_out
                                    }}</a>
                                <a :href="'http://www.dhl.com/en/express/tracking.html?brand=DHL&AWB=' + pkg.tracking_number_out"
                                    target="_blank" v-if="pkg.carrier_code === 'dhl'">{{ pkg.tracking_number_out }}</a>
                                <a :href="'https://www.ups.com/track?loc=en_US&tracknum=' + pkg.tracking_number_out + '&requester=WT%2Ftrackdetails'"
                                    target="_blank" v-if="pkg.carrier_code === 'ups'">{{ pkg.tracking_number_out }}</a>
                                <a :href="'https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=2&text28777=&tLabels=' + pkg.tracking_number_out + '%2C&tABt=false'"
                                    target="_blank" v-if="pkg.carrier_code === 'usps'">{{ pkg.tracking_number_out }}</a>
                            </p>
                        </div>

                    </div>

                    <!-- Right Section: Grand Total + Payment Status + Dropdown -->
                    <div class="flex flex-col whitespace-nowrap items-end">
                        <!-- Grand Total -->
                        <p class="text-[10px] sm:text-base font-semibold text-gray-800">
                            <span class="hidden sm:inline">Grand</span> Total:
                            <span class="text-gray-800">${{ pkg.grand_total }}</span>
                        </p>
                        <!-- Payment Status and Dropdown -->
                        <div class="flex items-center gap-3 mt-1">
                            <!-- Payment Status -->
                            <span
                                class="text-xs sm:text-sm font-semibold px-3 py-1 rounded-full border inline-flex items-center gap-1"
                                :class="{
                                    'border-green-900 bg-green-100 text-green-700': pkg.payment_status === 'Paid',
                                    'border-yellow-300 bg-yellow-100 text-yellow-700': pkg.payment_status !== 'Paid'
                                }">
                                <i v-if="pkg.payment_status === 'Paid'" class="fas fa-check-circle"></i>
                                <i v-else class="fas fa-clock"></i>
                                {{ pkg.payment_status }}
                            </span>

                            <!-- Dropdown Button -->
                            <button :id="`dropdownMenuIconButton-${pkg.id}`" @click="toggleDropdown(pkg.id)"
                                class="hidden sm:inline-flex p-2 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-gray-200 bg-white"
                                type="button">
                                <svg class="w-5 h-5 text-gray-700" xmlns="http://www.w3.org/2000/svg"
                                    fill="currentColor" viewBox="0 0 4 15">
                                    <path
                                        d="M3.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 6.041a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 5.959a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                                </svg>
                            </button>

                            <!-- Dropdown Menu -->
                            <div v-if="openDropdownId === pkg.id"
                                class="absolute z-20 mt-32 right-8 bg-white divide-y divide-gray-100 rounded-lg shadow w-44">
                                <div class="py-2">
                                    <a href="#" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        @click="reshipPackage(pkg.id)">Re-ship</a>
                                </div>
                                <ul v-if="pkg.payment_status === 'Paid'" class="py-2 text-sm text-gray-700">
                                    <template v-for="payment in pkg.payments" :key="payment.id">
                                        <li>
                                            <a :href="project_url + '/storage/payment-invoices/' + payment.id + '.pdf'"
                                                target="_blank" class="block px-4 py-2 hover:bg-gray-100">Print Invoice
                                                #{{ payment.id }}</a>
                                        </li>
                                    </template>
                                    <li>
                                        <a :href="project_url + '/storage/labels/' + pkg.label_access_code + '.pdf'"
                                            target="_blank" class="block px-4 py-2 hover:bg-gray-100">Print Label</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Card Body: Package Details | Sender | Receiver | Service -->
                <div class="p-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Package Details -->
                    <div class=" p-3 rounded-lg mb-4">

                        <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                            <h3 class="text-sm sm:text-base font-semibold font-inter text-gray-800"> Package Details
                            </h3>
                        </div>

                        <template v-for="box in pkg.boxes" :key="box.id">
                            <div class="bg-gray-50 p-2 rounded-lg mb-2">
                                <p class="text-xs text-gray-600 flex items-center gap-2">
                                    <span><b>Dimension:</b> {{ box.length }}×{{ box.width }}×{{ box.height }} {{
                                        box.dim_unit }}</span>
                                    <span class="text-gray-400">•</span>
                                    <span><b>Weight:</b> {{ box.weight }} {{ box.weight_unit }}</span>
                                </p>
                            </div>
                        </template>
                        <!-- Package Summary -->
                        <div class="mt-4">
                            <!-- Top border line -->
                            <div class="border-t border-gray-300 mb-2"></div>

                            <!-- Summary Details -->
                            <div class="flex flex-wrap gap-4 text-gray-700 text-sm">
                                <div class="flex items-center gap-1">
                                    <span class="font-medium text-gray-500">Total Packages:</span>
                                    <span class="font-semibold text-gray-800">{{ pkg.boxes.length }}</span>
                                </div>
                                <div class="flex items-center gap-1">
                                    <span class="font-medium text-gray-500">Total Weight:</span>
                                    <span class="font-semibold text-gray-800">
                                        {{pkg.boxes.reduce((total, box) => total + box.weight, 0)}} {{
                                            pkg.boxes[0]?.weight_unit }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>


                    <!-- Sender -->
                    <div class="p-3 rounded-lg" v-if="pkg?.ship_from">
                        <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                            <h3 class="text-sm sm:text-base font-semibold font-inter text-gray-800">Sender
                            </h3>
                        </div>
                        <div class="text-sm">
                            <p><b>Name:</b> {{ pkg.ship_from.fullname }}</p>
                            <p><b>Address:</b> {{ pkg.ship_from.address }}</p>
                            <p v-if="pkg.ship_from.address_2"><b>Address 2:</b> {{ pkg.ship_from.address_2 }}</p>
                            <p v-if="pkg.ship_from.address_3"><b>Address 3:</b> {{ pkg.ship_from.address_3 }}</p>
                            <p><b>Zip:</b> {{ pkg.ship_from.zip_code }}</p>
                            <p><b>City:</b> {{ pkg.ship_from.city }}</p>
                            <p><b>Country:</b> {{ pkg.ship_from.country?.name }}</p>
                        </div>
                    </div>

                    <!-- Receiver -->
                    <div class=" p-3 rounded-lg" v-if="pkg?.ship_to">
                        <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                            <h3 class="text-sm sm:text-base font-semibold font-inter text-gray-800"> Receiver
                            </h3>
                        </div>
                        <div class="text-sm">
                            <p><b>Name:</b> {{ pkg.ship_to.fullname }}</p>
                            <p><b>Address:</b> {{ pkg.ship_to.address }}</p>
                            <p v-if="pkg.ship_to.address_2"><b>Address 2:</b> {{ pkg.ship_to.address_2 }}</p>
                            <p v-if="pkg.ship_to.address_3"><b>Address 3:</b> {{ pkg.ship_to.address_3 }}</p>
                            <p><b>Zip:</b> {{ pkg.ship_to.zip_code }}</p>
                            <p><b>City:</b> {{ pkg.ship_to.city }}</p>
                            <p><b>Country:</b> {{ pkg.ship_to.country?.name }}</p>
                        </div>
                    </div>

                    <!-- Service -->
                    <!-- <div class=" p-3 rounded-lg">
                        <h3 class="bg-[#F8EACA] px-16 py-1.5 rounded-t-md font-semibold text-gray-800 mb-2 w-fit">
                            Service</h3>
                        <p class="text-sm text-gray-700 uppercase">
                            {{ pkg.carrier_code }} <br>
                            {{ pkg.service_label }} <br>
                            {{ pkg.package_type_code }}
                        </p>
                    </div> -->


                </div>
                <!-- Footer Actions -->
                <div class="bg-gray-50 px-4 py-3 border-t  
            flex justify-between flex-wrap gap-2
            rounded-b-2xl">
                    <!-- Left Side: Existing Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <template v-if="pkg.payment_status === 'Paid'">
                            <!-- Print Label -->
                            <a :href="`${project_url}/storage/labels/${pkg.label_access_code}.pdf`" target="_blank"
                                class="inline-flex items-center gap-1 px-1.5 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-green-500">
                                <i class="fas fa-print"></i>
                                Print Label
                            </a>

                            <!-- Payment Invoices -->
                            <template v-for="payment in pkg.payments" :key="payment.id">
                                <a :href="`${project_url}/storage/payment-invoices/${payment.id}.pdf`" target="_blank"
                                    class="inline-flex items-center gap-1 px-1.5 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium border border-gray-400 text-gray-800 bg-gray-100 rounded-full shadow-sm transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400">
                                    <i class="fas fa-file-invoice"></i>
                                    Invoice #{{ payment.id }}
                                </a>
                            </template>
                        </template>

                        <template v-else>
                            <!-- Edit Button -->
                            <router-link :to="{ name: 'account.shipments.edit', params: { id: pkg.id } }"
                                class="inline-flex items-center gap-1 px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-yellow-400 to-amber-600 rounded-full shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-yellow-500">
                                <i class="fas fa-edit"></i>
                                Edit
                            </router-link>
                        </template>
                    </div>


                    <!-- Right Side: Dropdown Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <button @click="reshipPackage(pkg.id)"
                            class="inline-flex items-center gap-1 px-1.5 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r border from-yellow-400 to-amber-600 rounded-full shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <i class="fas fa-truck"></i>
                            Re-ship
                        </button>

                        <!-- <template v-if="pkg.payment_status === 'Paid'">
                            <a :href="`${project_url}/storage/labels/${pkg.label_access_code}.pdf`" target="_blank"
                                class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-yellow-400 to-amber-600 rounded-full shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-yellow-500">
                                Print Label
                            </a>

                            <template v-for="payment in pkg.payments" :key="payment.id">
                                <a :href="`${project_url}/storage/payment-invoices/${payment.id}.pdf`" target="_blank"
                                    class="inline-flex items-center px-4 py-2 text-sm font-medium border border-gray-400 text-gray-800 bg-gray-100 rounded-full shadow-sm transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400">
                                    Invoice #{{ payment.id }}
                                </a>
                            </template>
                        </template> -->
                    </div>
                </div>

            </div>
        </template>

        <!-- Empty State -->
        <div v-else class="flex flex-col items-center justify-center text-center p-6 mt-10"
            style="margin-bottom: 150px;">
            <h1 class="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                There are no orders in the history.
            </h1>
            <img class="h-[65px] sm:h-[72px] w-[100px] sm:w-[120px] sm:object-contain mt-2" src="@/assets/logo.svg"
                alt="No Orders" />
        </div>

        <!-- Pagination -->
        <Pagination v-if="packages.data && packages.data.length > 0" :pagination="packages" @fetchRecords="fetchRecords"
            class="mt-6" />
    </div>
</template>

<script>
import Pagination from "@/components/Global/Pagination.vue";
import LoaderComponent from "@/components/Partials/LoaderComponent.vue";
// import ShipmentHeader from '@/components/Account/Shipment/ShipmentHeader.vue';

export default {
    name: "AccountOrder",
    data() {
        return {
            packages: [],
            loading: false,
            project_url: process.env.VUE_APP_PROJECT_URL,
            form: {
                package_id: "",
            },
            openDropdownId: null,
        };
    },
    components: { Pagination, LoaderComponent },

    methods: {
        fetchRecords() {
            this.loading = true;
            const url = "package/index";

            var filters = {
                package_status_id: [5]
            }

            this.$axios
                .post(url, filters, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.packages = response.data.data.packages;
                    this.loading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.loading = false;
                });
        },
        reshipPackage(package_id) {
            this.errors = {};
            this.loading = true;
            this.form.package_id = package_id;
            this.$axios
                .post("package/reship", this.form, {
                    headers: this.$headers
                })
                .then((response) => {
                    this.$router.push({ name: "account.shipments.edit", params: { id: response.data.data.package_id } })
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleDropdown(id) {
            this.openDropdownId = this.openDropdownId === id ? null : id;
        },
    },
    mounted() {
        this.fetchRecords();
    }
};
</script>